"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import type { GhostedPayload } from "@/app/lib/ghosted";

const statusColorMap: Record<string, string> = {
  online: "",
  idle: "",
  "dnd": "",
  offline: "",
};

function formatDate(value: string | null) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  return date.toLocaleString("en", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4 },
  },
};

export function StatusGrid() {
  const [data, setData] = useState<GhostedPayload | null>(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      try {
        const response = await fetch("/api/ghosted", { cache: "no-store" });
        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as GhostedPayload;
        if (!ignore) {
          setData(payload);
        }
      } catch {
      }
    }

    load();
    const interval = setInterval(load, 30_000);
    return () => {
      ignore = true;
      clearInterval(interval);
    };
  }, []);

  const status = data?.discord?.status ? data.discord.status : "offline";
  const statusColor = statusColorMap[status] ?? "bg-slate-500";
  const customStatus = data?.discord?.customStatus ?? "no custom status";
  const lastPlayedDate = formatDate(data?.currentTrack?.startedAt ?? data?.lastPlayed?.startedAt ?? null);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="mx-auto w-full max-w-xs grid grid-cols-2 gap-3"
    >
      <motion.div
        variants={itemVariants}
        className="rounded-lg bg-white/5 backdrop-blur-sm p-3 flex flex-col items-center justify-center text-center transition-all duration-300 hover:bg-white/8"      >
        <span className="text-[0.65rem] uppercase tracking-[0.24em] text-muted/60 mb-2">status</span>
        <div className="relative flex items-center justify-center w-full">
          <span className={`absolute left-1/2 -translate-x-[calc(100%+1.1rem)] h-2 w-2 rounded-full ${statusColor} animate-pulse`} />
          <span className="text-[0.85rem] font-medium text-foreground">{status}</span>
        </div>
      </motion.div>


      <motion.div
        variants={itemVariants}
        className="rounded-lg bg-white/5 backdrop-blur-sm p-3 flex flex-col items-center justify-center text-center transition-all duration-300 hover:bg-white/8"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.24em] text-muted/60 mb-1">last played</span>
        <span className="text-[0.75rem] text-foreground">{lastPlayedDate}</span>
      </motion.div>
    </motion.div>
  );
}
